/**
 * Meta API activity & limits — API client.
 *
 * Thin fetch wrapper matching the `audit-api.ts` / `stats-api.ts` conventions
 * (RTK Query is intentionally NOT used here for consistency with the rest of
 * the agency surface area). All requests use the agency Bearer token sourced
 * from Redux (`state.auth.agency.accessToken`).
 *
 * Backend contract:
 *   GET /api/v1/agency/meta-activity
 *       ?shop=&operation=&status=&from=&to=&page=&per_page=
 *     → { data: [ MetaActivityEntry ], meta: { total, per_page, current_page, last_page } }
 *   GET /api/v1/agency/meta-activity/summary?window=7d|30d&shop=
 *     → MetaActivitySummary
 *
 * Both endpoints are owner/team_lead only (the API 403s other roles).
 *
 * Responses are unwrapped defensively: the backend may return the payload bare
 * OR wrapped under a top-level `{ data }` envelope.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

/** Time window for the summary endpoint. */
export type MetaActivityWindow = '7d' | '30d';

/** Per-call status. */
export type MetaActivityStatus = 'ok' | 'throttled' | 'requeued' | 'failed';

/** Where the call originated. */
export type MetaActivitySource = 'mcp_read' | 'executor_write';

/** A single Meta API call entry. */
export interface MetaActivityEntry {
  id: string | number;
  created_at: string; // ISO8601
  shop_id: string | number | null;
  /** Human-readable shop name when the backend can resolve it. */
  shop_name?: string | null;
  /** Shop slug, when present (used to humanise against the accessible-shop list). */
  shop_slug?: string | null;
  /** Requesting employee (uuid), when the call carried attribution. */
  employee_id?: string | null;
  /** Org-scoped employee email resolved by the backend (preferred label). */
  employee_email?: string | null;
  operation: string;
  ad_account_id: string | null;
  http_method: string;
  status: MetaActivityStatus;
  duration_ms: number | null;
  /** Business-use-case usage at the time of the call (0–100). */
  buc_usage_pct: number | null;
  /** App-level usage at the time of the call (0–100). */
  app_usage_pct: number | null;
  error: string | null;
  source: MetaActivitySource;
}

export interface MetaActivityMeta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface MetaActivityResponse {
  data: MetaActivityEntry[];
  meta: MetaActivityMeta;
}

/** A single bucket in the usage-over-time timeline. */
export interface MetaActivityTimelineBucket {
  bucket_ts: string; // ISO8601
  calls: number;
  /** Peak BUC usage in this bucket (0–100). */
  max_buc_usage_pct: number | null;
}

export interface MetaActivitySummary {
  total_calls: number;
  by_status: {
    ok: number;
    throttled: number;
    requeued: number;
    failed: number;
  };
  throttle_events: number;
  requeue_events: number;
  /** Peak BUC usage across the window (0–100). */
  peak_buc_usage_pct: number | null;
  /** Peak app usage across the window (0–100). */
  peak_app_usage_pct: number | null;
  timeline: MetaActivityTimelineBucket[];
  /**
   * Is the audit pipeline FEEDING, or is the agency simply quiet?
   *
   * An empty table looks the same either way — which is how a producer outage
   * ran from 2026-07-13 to 2026-08-25 unnoticed. The backend compares the newest
   * audited Meta call against the newest Facebook tool call the gateway logged:
   * traffic arriving with no audit rows behind it means the pipeline is broken,
   * not idle. Optional so an older API build simply renders no banner.
   */
  pipeline?: MetaActivityPipelineHealth;
}

/** Freshness of the Meta audit pipeline for this org (see `MetaActivitySummary.pipeline`). */
export interface MetaActivityPipelineHealth {
  /** Newest audited Meta call, all time (ignores the window). Null = never any. */
  newest_call_at: string | null;
  /** Newest Facebook tool call the gateway logged, all time. Null = no traffic. */
  newest_tool_call_at: string | null;
  /** True when tool calls are landing but audit rows are not. */
  stale: boolean;
}

/** Filters accepted by `listMetaActivity`. All fields optional. */
export interface MetaActivityFilters {
  shop?: string;
  operation?: string;
  status?: MetaActivityStatus | '';
  /** ISO date (YYYY-MM-DD) or ISO8601. */
  from?: string;
  /** ISO date (YYYY-MM-DD) or ISO8601. */
  to?: string;
  page?: number;
  per_page?: number;
}

// ---------- internal helpers ----------

function buildHeaders(token: string | undefined): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[meta-activity-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  token: string | undefined,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...buildHeaders(token),
    ...((init.headers as Record<string, string>) || {}),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<T>(response);
}

/**
 * Unwrap a possibly-`{data}`-wrapped payload. Used for the summary endpoint
 * where the meaningful shape is the object itself (NOT `data`). The list
 * endpoint keeps its `{ data, meta }` envelope, so it does NOT use this helper
 * — see `normaliseList`.
 */
function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined &&
    // Summary payload carries `total_calls`; if it is present at the top level
    // we already have the unwrapped shape and must NOT descend into `data`.
    !('total_calls' in (raw as Record<string, unknown>))
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function buildSummaryQuery(window: MetaActivityWindow, shop?: string): string {
  const params = new URLSearchParams();
  params.set('window', window);
  if (shop) params.set('shop', shop);
  return params.toString();
}

function buildListQuery(filters: MetaActivityFilters): string {
  const params = new URLSearchParams();
  if (filters.shop) params.set('shop', filters.shop);
  if (filters.operation) params.set('operation', filters.operation);
  if (filters.status) params.set('status', filters.status);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.page && filters.page > 0) {
    params.set('page', String(filters.page));
  }
  if (filters.per_page && filters.per_page > 0) {
    params.set('per_page', String(filters.per_page));
  }
  return params.toString();
}

/**
 * Normalise the list response so callers always get `{ data, meta }`.
 *
 * Accepts:
 *   - the canonical `{ data: [...], meta: {...} }` envelope, or
 *   - a bare array of entries (defensive — backend variants), in which case we
 *     synthesise a single-page meta block.
 */
function normaliseList(
  raw: MetaActivityResponse | MetaActivityEntry[] | { data?: MetaActivityEntry[] },
  requestedPage: number,
  requestedPerPage: number
): MetaActivityResponse {
  // Bare array form.
  if (Array.isArray(raw)) {
    return {
      data: raw,
      meta: {
        total: raw.length,
        per_page: requestedPerPage,
        current_page: requestedPage,
        last_page: 1,
      },
    };
  }

  // Descend the ApiResponse envelope `{ success, message, data: <payload> }`.
  // central-api (ApiResponse::success) nests the list one level deeper than the
  // bare `{ data, meta }` this normaliser originally assumed. When `.data` is a
  // non-array object, the real list payload lives inside it; when `.data` is
  // already an array (or absent), the payload is top-level and we leave it
  // untouched — so bare and wrapped shapes both work and this can't regress.
  const obj = (raw ?? {}) as { data?: unknown; meta?: Partial<MetaActivityMeta> };
  const descended: unknown =
    obj.data != null && typeof obj.data === 'object' && !Array.isArray(obj.data)
      ? obj.data
      : obj;

  // The descended payload may itself be a bare array.
  if (Array.isArray(descended)) {
    return {
      data: descended as MetaActivityEntry[],
      meta: {
        total: descended.length,
        per_page: requestedPerPage,
        current_page: requestedPage,
        last_page: 1,
      },
    };
  }

  const inner = descended as { data?: unknown; meta?: Partial<MetaActivityMeta> };
  const data = Array.isArray(inner.data) ? (inner.data as MetaActivityEntry[]) : [];
  const meta: MetaActivityMeta = {
    total: inner.meta?.total ?? data.length,
    per_page: inner.meta?.per_page ?? requestedPerPage,
    current_page: inner.meta?.current_page ?? requestedPage,
    last_page: inner.meta?.last_page ?? 1,
  };
  return { data, meta };
}

/** Defensive defaults for the summary shape so the UI never reads undefined. */
function normaliseSummary(
  raw: Partial<MetaActivitySummary> | undefined
): MetaActivitySummary {
  const s = raw ?? {};
  const by: Partial<MetaActivitySummary['by_status']> = s.by_status ?? {};
  return {
    total_calls: s.total_calls ?? 0,
    by_status: {
      ok: by.ok ?? 0,
      throttled: by.throttled ?? 0,
      requeued: by.requeued ?? 0,
      failed: by.failed ?? 0,
    },
    throttle_events: s.throttle_events ?? 0,
    requeue_events: s.requeue_events ?? 0,
    peak_buc_usage_pct: s.peak_buc_usage_pct ?? null,
    peak_app_usage_pct: s.peak_app_usage_pct ?? null,
    timeline: Array.isArray(s.timeline) ? s.timeline : [],
  };
}

// ---------- public API ----------

/**
 * Fetch a paginated, filtered slice of the agency's Meta API activity feed.
 * Backend enforces owner/team_lead role (403 otherwise).
 */
export async function listMetaActivity(
  filters: MetaActivityFilters = {},
  token?: string
): Promise<MetaActivityResponse> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const requestedPage = filters.page && filters.page > 0 ? filters.page : 1;
  const requestedPerPage =
    filters.per_page && filters.per_page > 0 ? filters.per_page : 25;
  const qs = buildListQuery({
    ...filters,
    page: requestedPage,
    per_page: requestedPerPage,
  });
  const raw = await request<
    MetaActivityResponse | MetaActivityEntry[] | { data?: MetaActivityEntry[] }
  >(`agency/meta-activity?${qs}`, t, { method: 'GET' });
  return normaliseList(raw, requestedPage, requestedPerPage);
}

/**
 * Fetch the rolled-up Meta API activity summary for the acting employee's org.
 * Backend enforces owner/team_lead role (403 otherwise).
 */
export async function getMetaActivitySummary(
  window: MetaActivityWindow,
  shop?: string,
  token?: string
): Promise<MetaActivitySummary> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const raw = await request<
    MetaActivitySummary | { data?: MetaActivitySummary }
  >(`agency/meta-activity/summary?${buildSummaryQuery(window, shop)}`, t, {
    method: 'GET',
  });
  return normaliseSummary(unwrap<MetaActivitySummary>(raw));
}
